// /hotel/:slug — SSR head() with real title/description/JSON-LD from
// hotel_static_pages, while the body renders through the App bridge.
import { createFileRoute, notFound } from "@tanstack/react-router";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/lib/route-boundaries";
import { getHost } from "@/lib/host";
import HotelStaticPage from "@/pages/HotelStaticPage";
import { supabase } from "@/integrations/supabase/client";
import {
  canonicalOriginFromHost,
  localeFromHost,
  type LocaleKey,
} from "@/lib/locale-from-host";
import { buildHreflangLinks } from "@/lib/hreflang";
import { getOptimizedUrl } from "@/utils/imageOptimization";

type HotelSEO = {
  name: string;
  city: string | null;
  country: string | null;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  star_rating: number | null;
  description: string | null;
  image: string | null;
  slug: string;
};

function pickDescription(
  locale: LocaleKey,
  row: {
    description_es: string | null;
    description_en: string | null;
    description_pt: string | null;
  }
): string | null {
  const raw =
    locale === "pt-BR"
      ? row.description_pt
      : locale === "en" || locale === "en-EU"
        ? row.description_en
        : row.description_es;
  if (!raw) return null;
  const plain = raw.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  if (!plain) return null;
  return plain.length > 160 ? `${plain.slice(0, 157).trimEnd()}…` : plain;
}

const BRAND_SUFFIX = " | Fora da Rota";
const MAX_TITLE = 60;

function buildHotelTitle(name: string, locationSuffix: string): string {
  const full = locationSuffix ? `${name} — ${locationSuffix}${BRAND_SUFFIX}` : `${name}${BRAND_SUFFIX}`;
  if (full.length <= MAX_TITLE) return full;
  const noLoc = `${name}${BRAND_SUFFIX}`;
  if (noLoc.length <= MAX_TITLE) return noLoc;
  const budget = MAX_TITLE - BRAND_SUFFIX.length - 1;
  return `${name.slice(0, Math.max(1, budget)).trimEnd()}…${BRAND_SUFFIX}`;
}

function buildHotelDescription(
  locale: LocaleKey,
  name: string,
  locationSuffix: string,
  base: string | null
): string {
  const inLoc = locationSuffix ? (locale === "pt-BR" ? ` em ${locationSuffix}` : locale === "en" || locale === "en-EU" ? ` in ${locationSuffix}` : ` en ${locationSuffix}`) : "";
  const fallback =
    locale === "pt-BR"
      ? `Reserve ${name}${inLoc} com a Fora da Rota: melhores preços, disponibilidade em tempo real e atendimento personalizado para sua viagem.`
      : locale === "en" || locale === "en-EU"
        ? `Book ${name}${inLoc} with Fora da Rota: best rates, real-time availability and personalized service to plan your trip with confidence.`
        : `Reserva ${name}${inLoc} con Fora da Rota: mejores precios, disponibilidad en tiempo real y atención personalizada para planear tu viaje.`;
  let text = base && base.length >= 50 ? base : fallback;
  if (text.length > 160) text = `${text.slice(0, 157).trimEnd()}…`;
  return text;
}


export const Route = createFileRoute("/hotel/$slug")({
  loader: async ({ params }) => {
    const host = getHost();
    const locale = localeFromHost(host);
    const origin = canonicalOriginFromHost(host);

    const { data, error } = await supabase
      .from("hotel_static_pages")
      .select(
        "slug,name,city,country,address,latitude,longitude,star_rating,images,description_es,description_en,description_pt"
      )
      .eq("slug", params.slug)
      .maybeSingle();

    if (error || !data) throw notFound();

    const images = Array.isArray(data.images)
      ? (data.images.filter((i) => typeof i === "string") as string[])
      : [];

    const hotel: HotelSEO = {
      slug: data.slug,
      name: data.name,
      city: data.city,
      country: data.country,
      address: data.address,
      latitude: data.latitude != null ? Number(data.latitude) : null,
      longitude: data.longitude != null ? Number(data.longitude) : null,
      star_rating: data.star_rating,
      description: pickDescription(locale, data),
      image: images[0] ?? null,
    };

    return { hotel, locale, origin };
  },
  head: ({ params, loaderData }) => {
    const origin = loaderData?.origin ?? "https://www.foradarota.com";
    const url = `${origin}/hotel/${params.slug}`;

    if (!loaderData) {
      return {
        meta: [
          { title: "Fora da Rota" },
          { name: "robots", content: "noindex" },
        ],
      };
    }

    const { hotel, locale } = loaderData;
    const locationSuffix = [hotel.city, hotel.country].filter(Boolean).join(", ");
    const title = buildHotelTitle(hotel.name, locationSuffix);
    const description = buildHotelDescription(locale, hotel.name, locationSuffix, hotel.description);
    const image = hotel.image ?? `${origin}/og-image.jpg`;


    const jsonLd: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "Hotel",
      name: hotel.name,
      url,
      description,
      image,
    };
    if (hotel.star_rating) {
      jsonLd.starRating = { "@type": "Rating", ratingValue: hotel.star_rating };
    }
    if (hotel.address || hotel.city || hotel.country) {
      jsonLd.address = {
        "@type": "PostalAddress",
        streetAddress: hotel.address ?? undefined,
        addressLocality: hotel.city ?? undefined,
        addressCountry: hotel.country ?? undefined,
      };
    }
    if (hotel.latitude != null && hotel.longitude != null) {
      jsonLd.geo = {
        "@type": "GeoCoordinates",
        latitude: hotel.latitude,
        longitude: hotel.longitude,
      };
    }

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:image", content: image },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: image },
      ],
      links: [
        { rel: "canonical", href: url },
        ...buildHreflangLinks(`/hotel/${params.slug}`),
        ...(hotel.image
          ? [
              {
                rel: "preload",
                as: "image",
                href: getOptimizedUrl(hotel.image, 640, 55),
                imagesrcset: [400, 640, 960, 1280, 1920]
                  .map((w) => `${getOptimizedUrl(hotel.image!, w, w <= 640 ? 55 : 65)} ${w}w`)
                  .join(", "),
                imagesizes: "(max-width: 768px) 100vw, 50vw",
                fetchpriority: "high",
              },
            ]
          : []),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(jsonLd),
        },
      ],
    };
  },
  component: HotelStaticPage,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: () => <RouteNotFoundBoundary message="Este hotel no está disponible." />,
});
